/**
 * Pins on a map, grouped by how close they sit at the current zoom. Greedy and deterministic: points are visited in
 * a fixed order and join the first cluster within `radius` of their centre, so the same pins make the same clusters,
 * and shrinking the radius (zooming in) splits them.
 */
export interface Point<T> {
  x: number;
  y: number;
  item: T;
}

export interface Cluster<T> {
  key: string;
  /** Mean position of the members. */
  x: number;
  y: number;
  items: T[];
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

export function clusterPoints<T>(points: Point<T>[], radius: number, keyOf: (item: T) => string): Cluster<T>[] {
  const sorted = points.slice().sort((a, b) => a.y - b.y || a.x - b.x || keyOf(a.item).localeCompare(keyOf(b.item)));
  const out: Cluster<T>[] = [];
  const r2 = radius * radius;
  for (const p of sorted) {
    let best: Cluster<T> | null = null;
    let bestD = Infinity;
    for (const c of out) {
      const dx = c.x - p.x;
      const dy = c.y - p.y;
      const d = dx * dx + dy * dy;
      if (d <= r2 && d < bestD) {
        best = c;
        bestD = d;
      }
    }
    if (best) {
      const n = best.items.length;
      best.x = (best.x * n + p.x) / (n + 1);
      best.y = (best.y * n + p.y) / (n + 1);
      best.items.push(p.item);
      best.minX = Math.min(best.minX, p.x);
      best.maxX = Math.max(best.maxX, p.x);
      best.minY = Math.min(best.minY, p.y);
      best.maxY = Math.max(best.maxY, p.y);
    } else {
      out.push({ key: keyOf(p.item), x: p.x, y: p.y, items: [p.item], minX: p.x, maxX: p.x, minY: p.y, maxY: p.y });
    }
  }
  return out;
}

/** Whether every member sits at the same spot (one city), so zooming further would never split it. */
export function isSinglePlace<T>(c: Cluster<T>, epsilon = 1e-6): boolean {
  return c.maxX - c.minX <= epsilon && c.maxY - c.minY <= epsilon;
}

/** The zoom that fits a cluster's spread into about a third of the view, never below the current one; one step in for a single place. */
export function zoomToFit<T>(c: Cluster<T>, view: { width: number; height: number }, current: number, max: number): number {
  if (isSinglePlace(c)) return Math.min(max, current * 1.5);
  const spanX = Math.max(c.maxX - c.minX, 1e-9);
  const spanY = Math.max(c.maxY - c.minY, 1e-9);
  const fit = Math.min(view.width / (spanX * 3), view.height / (spanY * 3));
  return Math.min(max, Math.max(current * 1.5, fit));
}

/** Great-circle distance between two places, in kilometres. */
export function distanceKm(aLat: number, aLon: number, bLat: number, bLon: number): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(bLat - aLat);
  const dLon = toRad(bLon - aLon);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLon / 2) ** 2;
  return 2 * 6371 * Math.asin(Math.min(1, Math.sqrt(h)));
}
